import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { Search, ShieldCheck, ShieldAlert } from 'lucide-react';
import { AppShell } from '../../components/AppShell';
import { Card } from '../../components/ui/Card';
import { Pill } from '../../components/ui/Pill';
import { api } from '../../lib/api';
import { formatDate } from '../../lib/format';

interface Record {
  id: string;
  record_kind: 'ap_invoice' | 'ar_invoice' | 'expense_receipt' | 'contract' | 'other';
  record_id: string;
  file_url: string;
  file_hash: string;
  tsa_synthetic: boolean;
  retention_through: string;
  search_keywords: string;
  created_at: string;
}

interface VerifyResult {
  ok: boolean;
  tokenPresent: boolean;
  synthetic: boolean;
  genTime?: string;
  serial?: string;
}

export function DigitalRecordsPage() {
  const { wsId } = useParams<{ wsId: string }>();
  const { t, i18n } = useTranslation();
  const [q, setQ] = useState('');
  const [verify, setVerify] = useState<{ [id: string]: VerifyResult | 'loading' }>({});

  const runVerify = async (id: string) => {
    setVerify((v) => ({ ...v, [id]: 'loading' }));
    try {
      const r = await api.get<VerifyResult>(`/workspaces/${wsId}/digital-records/${id}/verify-timestamp`);
      setVerify((v) => ({ ...v, [id]: r.data }));
    } catch {
      setVerify((v) => ({ ...v, [id]: { ok: false, tokenPresent: false, synthetic: true } }));
    }
  };

  const list = useQuery<Record[]>({
    queryKey: ['digital-records', wsId, q],
    enabled: !!wsId,
    queryFn: async () => {
      if (q.trim()) {
        return (await api.get<Record[]>(`/workspaces/${wsId}/digital-records/search?q=${encodeURIComponent(q.trim())}`)).data;
      }
      return (await api.get<Record[]>(`/workspaces/${wsId}/digital-records`)).data;
    },
  });

  return (
    <AppShell>
      <div className="px-8 py-7 max-w-6xl">
        <div className="flex items-center justify-between mb-2">
          <h1 className="display text-2xl font-semibold">{t('digitalRecords.title')}</h1>
          <div className="flex items-center gap-2 h-9 px-3 rounded-lg border border-ink-300 dark:border-ink-700 bg-white dark:bg-ink-900">
            <Search size={14} className="text-ink-400" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={t('digitalRecords.searchPlaceholder')}
              className="bg-transparent border-0 outline-none text-sm w-64"
            />
          </div>
        </div>
        <p className="text-sm text-ink-500 mb-6">{t('digitalRecords.subtitle')}</p>

        <Card>
          {list.isLoading ? (
            <div className="p-10 text-center text-ink-500">{t('common.loading')}</div>
          ) : !list.data || list.data.length === 0 ? (
            <div className="p-10 text-center text-ink-500 text-sm">{t('digitalRecords.empty')}</div>
          ) : (
            <ul className="divide-y divide-ink-200 dark:divide-ink-800">
              {list.data.map((r) => (
                <li key={r.id} className="px-5 py-3 flex items-center justify-between text-sm">
                  <div className="min-w-0">
                    <div className="font-medium">{t(`digitalRecords.kind.${r.record_kind}`)}</div>
                    <div className="text-xs text-ink-500 font-mono">{r.file_hash.slice(0, 16)}…</div>
                    <div className="text-xs text-ink-500 mt-0.5 truncate">{r.search_keywords}</div>
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    {(() => {
                      const res = verify[r.id];
                      if (res === 'loading') return <span className="text-xs text-ink-400">…</span>;
                      if (res) {
                        const tone = res.ok && !res.synthetic ? 'success' : res.ok ? 'accent' : 'warn';
                        const label = !res.tokenPresent
                          ? t('digitalRecords.tsaSynthetic')
                          : res.ok
                            ? t('digitalRecords.tsaVerified')
                            : t('digitalRecords.tsaFailed');
                        return (
                          <span title={res.genTime ?? ''}>
                            <Pill tone={tone}>
                              {res.ok && !res.synthetic ? <ShieldCheck size={11} className="inline mr-1" /> : <ShieldAlert size={11} className="inline mr-1" />}
                              {label}
                            </Pill>
                          </span>
                        );
                      }
                      return (
                        <button
                          type="button"
                          onClick={() => runVerify(r.id)}
                          className="inline-flex items-center gap-1 text-xs text-accent-600 hover:text-accent-700"
                        >
                          <ShieldCheck size={12} />
                          {t('digitalRecords.verifyTimestamp')}
                        </button>
                      );
                    })()}
                    <span className="text-[11px] uppercase tracking-wide text-ink-500">
                      {t('digitalRecords.retentionUntil')}
                    </span>
                    <Pill tone="muted">{formatDate(r.retention_through, i18n.language)}</Pill>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>
    </AppShell>
  );
}
